import { Component } from "react"
import confetti from "canvas-confetti"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import PlanContent from "./PlanContent"
import "./PremiumPage.css"


interface PremiumPageProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
}

interface PremiumPageState {
    redirectSuccess: boolean
    redirectEmail: string | null
    redirectExistingUser: boolean
}


class PremiumPage extends Component<PremiumPageProps, PremiumPageState> {
    private confettiCanvas: HTMLCanvasElement | null = null

    constructor(props: PremiumPageProps) {
        super(props)
        const urlParams = new URLSearchParams(window.location.search)
        const isSuccess = urlParams.get("success") === "true" || urlParams.get("redirect_status") === "succeeded"
        this.state = {
            redirectSuccess: isSuccess,
            redirectEmail: urlParams.get("email"),
            redirectExistingUser: urlParams.get("existing") === "true",
        }
    }

    componentDidMount() {
        const hasPending = !!(
            this.props.stateHandler.get(StateConstants.PENDING_PAYMENT_INTENT_ID)
            || localStorage.getItem("pendingPaymentIntentId")
        )
        if (hasPending) {
            this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
        }

        if (this.state.redirectSuccess) {
            this.fireConfetti()
            this.refreshUserStatus()
        }
    }

    componentWillUnmount() {
        if (this.confettiCanvas) {
            document.body.removeChild(this.confettiCanvas)
            this.confettiCanvas = null
        }
    }

    async refreshUserStatus() {
        const maxAttempts = 5
        const delayMs = 2000

        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(resolve => setTimeout(resolve, delayMs))
            this.props.apiHandler.clearCache()
            try {
                const userInfo = await this.props.apiHandler.getUserInfo()
                const features: string[] = userInfo?.features || []
                if (features.length > 0) {
                    this.props.stateHandler.set(StateConstants.USER_FEATURES, features)
                    this.props.stateHandler.set(StateConstants.ACCOUNT_TYPE, userInfo?.at || "basic")
                    this.props.stateHandler.set(StateConstants.IS_PREMIUM, true)
                    return
                }
            } catch (error) {
                // Retry
            }
        }
    }

    fireConfetti() {
        if (sessionStorage.getItem("confettiShown")) return
        sessionStorage.setItem("confettiShown", "true")

        if (!this.confettiCanvas) {
            this.confettiCanvas = document.createElement("canvas")
            this.confettiCanvas.style.position = "fixed"
            this.confettiCanvas.style.top = "0"
            this.confettiCanvas.style.left = "0"
            this.confettiCanvas.style.width = "100%"
            this.confettiCanvas.style.height = "100%"
            this.confettiCanvas.style.pointerEvents = "none"
            this.confettiCanvas.style.zIndex = "999999"
            document.body.appendChild(this.confettiCanvas)
        }

        const myConfetti = confetti.create(this.confettiCanvas, { resize: true })
        const duration = 3000
        const end = Date.now() + duration
        const frame = () => {
            myConfetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 } })
            myConfetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 } })
            if (Date.now() < end) {
                requestAnimationFrame(frame)
            } else {
                setTimeout(() => {
                    if (this.confettiCanvas) {
                        document.body.removeChild(this.confettiCanvas)
                        this.confettiCanvas = null
                    }
                }, 2000)
            }
        }
        frame()
    }

    render() {
        const urlParams = new URLSearchParams(window.location.search)

        if (this.state.redirectSuccess && this.state.redirectEmail) {
            return (
                <div className="premium-page">
                    <div className="plan-post-payment">
                        <h3 className="plan-post-payment-title">You're in!</h3>
                        <p className="plan-post-payment-message">
                            {this.state.redirectExistingUser
                                ? <>You already have an account with <strong>{this.state.redirectEmail}</strong>. Log in to activate your Lifetime Access.</>
                                : <>We've sent a temporary password to <strong>{this.state.redirectEmail}</strong>. Check your email and log in to activate your Lifetime Access.</>
                            }
                        </p>
                        <button
                            className="plan-cta-button"
                            onClick={() => {
                                this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                            }}
                        >
                            Go to Login
                        </button>
                    </div>
                </div>
            )
        }

        return (
            <div className="premium-page">
                <PlanContent
                    stateHandler={this.props.stateHandler}
                    apiHandler={this.props.apiHandler}
                    showSuccess={this.state.redirectSuccess}
                    showCancelled={urlParams.get("cancelled") === "true" || urlParams.get("redirect_status") === "failed"}
                />
            </div>
        )
    }
}

export default PremiumPage
